import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { MessageProvider } from './context/MessageContext';
import { NetworkProvider } from './context/NetworkContext';
import { LocationProvider } from './context/LocationContext';
import { ChatDrawerProvider } from './context/ChatDrawerContext';
import { CssBaseline, Container } from '@mui/material';

// Auth
import ManagerAreaRedirect from './components/ManagerAreaRedirect';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import PrivateRoute from './components/auth/PrivateRoute';

// Layout
import AppLayout from './components/layout/AppLayout';
import OfflineIndicator from './components/OfflineIndicator';

// Manager Screens
import RootRedirect from './components/RootRedirect';
import MapDashboard from './components/dashboard/MapDashboard';
import ScheduleView from './components/schedule/ScheduleView';
import JobsScreen from './components/jobs/JobsScreen';
import TeamScreen from './components/team/TeamScreen';
import TimesheetManager from './components/team/TimesheetManager';
import LeaveApprovalManager from './components/team/LeaveApprovalManager';
import TeamMemberForm from './components/team/TeamMemberForm';
import WorkerDetailView from './components/team/WorkerDetailView';
import MoreScreen from './components/more/MoreScreen';
import MessagesHub from './components/messages/MessagesHub';
import ConversationView from './components/messages/ConversationView';

// Shared Screens
import Settings from './components/settings/Settings';
import JobForm from './components/jobs/JobForm';
import JobDetailView from './components/jobs/JobDetailView';
import AnalyticsDashboard from './components/dashboard/AnalyticsDashboard';
import Chatbot from './components/Chatbot';
import LeaveRequest from './components/worker/LeaveRequest';

// Client Screens
import ClientsScreen from './components/clients/ClientsScreen';
import ClientDetailView from './components/clients/ClientDetailView';
import ClientForm from './components/clients/ClientForm';

// Worker Screens
import WorkerDashboard from './components/worker/WorkerDashboard';
import WorkerSchedule from './components/worker/WorkerSchedule';
import WorkerTimesheet from './components/worker/WorkerTimesheet';

import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';

function App() {
  return (
    <NetworkProvider>
      <AuthProvider>
        <MessageProvider>
          <LocationProvider>
            <ChatDrawerProvider>
            <ThemeProvider theme={theme}>
            <Router>
            <CssBaseline />
            <OfflineIndicator />
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* Protected Routes */}
              <Route element={<PrivateRoute />}>
                <Route path="/" element={<RootRedirect />} />

                {/* Manager area — the server-rendered planner is canonical;
                    these routes bounce before rendering anything */}
                <Route element={<ManagerAreaRedirect />}>
                <Route path="/map" element={<AppLayout title="Live Map"><MapDashboard /></AppLayout>} />
                <Route path="/schedule" element={<AppLayout title="Schedule"><ScheduleView /></AppLayout>} />
                <Route path="/jobs" element={<AppLayout title="Jobs"><JobsScreen /></AppLayout>} />
                <Route path="/jobs/attention" element={<AppLayout title="Needs Attention"><JobsScreen /></AppLayout>} />
                <Route path="/jobs/pool" element={<AppLayout title="Job Pool"><JobsScreen /></AppLayout>} />
                <Route path="/jobs/scheduled" element={<AppLayout title="Scheduled Jobs"><JobsScreen /></AppLayout>} />
                <Route path="/jobs/completed" element={<AppLayout title="Completed Jobs"><JobsScreen /></AppLayout>} />
                <Route path="/jobs/issues" element={<AppLayout title="Unresolved Issues"><JobsScreen /></AppLayout>} />
                <Route path="/jobs/failed" element={<AppLayout title="Failed Jobs"><JobsScreen /></AppLayout>} />
                <Route path="/jobs/new" element={<AppLayout title="New Job"><JobForm /></AppLayout>} />
                <Route path="/jobs/:id" element={<AppLayout title="Job Details"><JobDetailView /></AppLayout>} />
                <Route path="/jobs/:id/edit" element={<AppLayout title="Edit Job"><JobForm /></AppLayout>} />
                <Route path="/team" element={<AppLayout title="Team"><TeamScreen /></AppLayout>} />
                <Route path="/team/new" element={<AppLayout title="Add Team Member"><TeamMemberForm /></AppLayout>} />
                <Route path="/team/timesheets" element={<AppLayout title="Timesheets"><TimesheetManager /></AppLayout>} />
                <Route path="/team/leave" element={<AppLayout title="Leave Management"><LeaveApprovalManager /></AppLayout>} />
                <Route path="/team/:id" element={<AppLayout title="Team Member"><WorkerDetailView /></AppLayout>} />
                <Route path="/messages" element={<AppLayout title="Messages"><MessagesHub /></AppLayout>} />
                <Route path="/messages/:channelType/:participantId" element={<AppLayout title="Conversation"><ConversationView /></AppLayout>} />
                <Route path="/more" element={<AppLayout title="More"><MoreScreen /></AppLayout>} />
                
                {/* Settings Routes */}
                <Route path="/settings" element={<AppLayout title="Settings"><Settings /></AppLayout>} />
                <Route path="/settings/*" element={<AppLayout title="Settings"><Settings /></AppLayout>} />
                
                {/* Tool Routes */}
                <Route path="/chat" element={<AppLayout title="AI Chat"><Container maxWidth="sm" sx={{ pt: 2 }}><Chatbot /></Container></AppLayout>} />
                <Route path="/analytics" element={<AppLayout title="Analytics"><AnalyticsDashboard /></AppLayout>} />
                <Route path="/clients" element={<AppLayout title="Clients"><ClientsScreen /></AppLayout>} />
                <Route path="/clients/new" element={<AppLayout title="New Client"><ClientForm /></AppLayout>} />
                <Route path="/clients/:id" element={<AppLayout title="Client Details"><ClientDetailView /></AppLayout>} />
                <Route path="/clients/:id/edit" element={<AppLayout title="Edit Client"><ClientForm /></AppLayout>} />
                </Route>
                
                {/* Shared / Worker Routes */}
                <Route path="/leave" element={<AppLayout title="Leave"><LeaveRequest /></AppLayout>} />
                <Route path="/worker" element={<AppLayout title="My Jobs"><WorkerDashboard /></AppLayout>} />
                <Route path="/worker/schedule" element={<AppLayout title="My Schedule"><WorkerSchedule /></AppLayout>} />
                <Route path="/worker/time" element={<AppLayout title="Timesheet"><WorkerTimesheet /></AppLayout>} />
                <Route path="/worker/messages" element={<AppLayout title="Messages"><MessagesHub /></AppLayout>} />
                <Route path="/worker/messages/:channelType/:participantId" element={<AppLayout title="Conversation"><ConversationView /></AppLayout>} />
                <Route path="/worker/more" element={<AppLayout title="More"><MoreScreen /></AppLayout>} />
              </Route>
              
              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Router>
          </ThemeProvider>
          </ChatDrawerProvider>
          </LocationProvider>
        </MessageProvider>
      </AuthProvider>
    </NetworkProvider>
  );
}

export default App;
